import React, { useContext, useCallback } from 'react';
import { AppContext } from '../context/AppContext';
import SearchModal from './SearchModal';

/**
 * Sale search modal with lazy loading
 * Shows contact, price, products, and date for easy selection
 * @param {boolean} open - Whether the modal is open
 * @param {function} onClose - Callback when modal is closed
 * @param {function} onSelect - Callback when a sale is selected
 * @param {number} selectedId - Currently selected sale ID (for highlighting)
 * @param {array} excludeIds - Array of sale IDs to exclude from results
 */
const SaleSearchModal = ({
  open,
  onClose,
  onSelect,
  selectedId = null,
  excludeIds = []
}) => {
  const { token } = useContext(AppContext);

  const calculateTotal = (items) => {
    return items?.reduce((sum, item) => sum + (item.price * item.qty), 0) || 0;
  };

  const fetchSales = useCallback(async (searchTerm, page, limit) => {
    try {
      // Fetch sales and contacts in parallel
      // Use all=true to get simple array without pagination
      const [salesRes, contactsRes] = await Promise.all([
        fetch('/api/sales?all=true', {
          headers: token ? { Authorization: token } : {}
        }),
        fetch('/api/contacts?all=true', {
          headers: token ? { Authorization: token } : {}
        })
      ]);

      if (!salesRes.ok) {
        throw new Error('Failed to fetch sales');
      }

      let sales = await salesRes.json();
      let contactsData = contactsRes.ok ? await contactsRes.json() : [];

      // Handle paginated response format (contacts may return { contacts: [...] })
      const contacts = Array.isArray(contactsData) ? contactsData : (contactsData.contacts || []);

      // Create a contact lookup map
      const contactMap = {};
      contacts.forEach(c => {
        contactMap[c.id] = c;
      });

      // Enhance sales with contact info
      sales = sales.map(sale => ({
        ...sale,
        contact: contactMap[sale.contactId] || null,
        total: calculateTotal(sale.items)
      }));

      // Filter out excluded sales
      if (excludeIds.length > 0) {
        sales = sales.filter(s => !excludeIds.includes(s.id));
      }

      // Client-side search filtering
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        sales = sales.filter(sale => {
          const contactName = sale.contact
            ? `${sale.contact.firstName || ''} ${sale.contact.lastName || ''}`.toLowerCase()
            : '';
          const productNames = (sale.items || [])
            .map(item => (item.name || '').toLowerCase())
            .join(' ');
          const dateStr = new Date(sale.createdAt).toLocaleDateString().toLowerCase();
          const priceStr = sale.total.toFixed(2);

          return (
            contactName.includes(term) ||
            productNames.includes(term) ||
            dateStr.includes(term) ||
            priceStr.includes(term)
          );
        });
      }

      // Sort by date (most recent first)
      sales.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      // Pagination
      const start = (page - 1) * limit;
      const paginatedSales = sales.slice(start, start + limit);

      return {
        items: paginatedSales,
        total: sales.length
      };
    } catch (error) {
      console.error('Error fetching sales:', error);
      return { items: [], total: 0 };
    }
  }, [token, excludeIds]);

  const columns = [
    {
      key: 'contact',
      label: 'Contact',
      render: (sale) => (
        <div className="font-medium text-gray-900">
          {sale.contact
            ? `${sale.contact.firstName} ${sale.contact.lastName}`
            : '-'}
        </div>
      )
    },
    {
      key: 'total',
      label: 'Price',
      render: (sale) => (
        <span className="font-semibold text-gray-900">
          ${sale.total.toFixed(2)}
        </span>
      )
    },
    {
      key: 'products',
      label: 'Products/Services',
      render: (sale) => (
        <div className="max-w-xs">
          {sale.items?.length > 0 ? (
            <ul className="list-disc list-inside text-gray-600">
              {sale.items.slice(0, 3).map((item, idx) => (
                <li key={idx} className="truncate text-sm">
                  {item.name} x{item.qty}
                </li>
              ))}
              {sale.items.length > 3 && (
                <li className="text-gray-400 text-sm">
                  +{sale.items.length - 3} more
                </li>
              )}
            </ul>
          ) : (
            <span className="text-gray-400">No items</span>
          )}
        </div>
      )
    },
    {
      key: 'date',
      label: 'Date',
      render: (sale) => (
        <span className="text-gray-600">
          {new Date(sale.createdAt).toLocaleDateString()}
        </span>
      )
    }
  ];

  return (
    <SearchModal
      open={open}
      onClose={onClose}
      onSelect={onSelect}
      title="Select Sale"
      searchPlaceholder="Search by contact, product, date, or price..."
      fetchItems={fetchSales}
      columns={columns}
      getItemId={(sale) => sale.id}
      emptyMessage="No sales found"
      selectedId={selectedId}
    />
  );
};

export default SaleSearchModal;
